import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Loader2, Shield, Eye, Lock, CheckCircle, Play, ChevronRight } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { FAQSection } from '@/components/FAQSection';
import { ThemeToggle } from '@/components/ThemeToggle';
import logo from '@/assets/logo.png';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { 
  generateSoftwareApplicationSchema, 
  generateOrganizationSchema,
  generateFAQSchema,
  SITE_URL 
} from '@/lib/structuredData';

// Simple animation variants - always visible by default
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] as const } }
};

const slideUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] as const } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const } }
};

// Animated counter component
const AnimatedNumber = ({ value, suffix = '', prefix = '' }: { value: number; suffix?: string; prefix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{count.toLocaleString('en-IN')}{suffix}
    </span>
  );
};

const Index = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const proofRef = useRef(null);
  const defenseRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const isProofInView = useInView(proofRef, { once: true, margin: "-100px" });
  const isDefenseInView = useInView(defenseRef, { once: true, margin: "-100px" });

  // FAQ data for schema
  const faqData = [
    { question: "What is Sentinel?", answer: "Sentinel is a financial intelligence platform that automates reconciliation, prevents duplicate payments, and detects invoice fraud with 99.2% accuracy." },
    { question: "How does duplicate payment detection work?", answer: "Our system analyzes invoice patterns, vendor histories, amounts, and dates to identify potential duplicates before payment is made, saving companies millions in erroneous payments." },
    { question: "Does Sentinel integrate with Tally?", answer: "Yes, Sentinel seamlessly integrates with Tally ERP via CSV import, allowing you to reconcile Tally ledger entries with bank statements automatically." },
    { question: "What is the pricing for Sentinel?", answer: "Sentinel offers a free tier with 100 transactions/month, Starter at ₹999/month for 500 transactions, and Pro at ₹2,499/month for unlimited transactions with advanced features." },
    { question: "How accurate is the matching algorithm?", answer: "Sentinel achieves 99.2% matching accuracy using multi-factor confidence scoring that considers amount, date, narration similarity, and vendor identity." }
  ];

  const structuredData = [
    generateSoftwareApplicationSchema({ free: true, price: 0 }),
    generateOrganizationSchema(),
    generateFAQSchema(faqData)
  ];

  useEffect(() => {
    if (user && !loading) {
      navigate('/inbox');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // The three layers of defense
  const defenseLayers = [
    {
      icon: Eye,
      title: 'Surveillance',
      stat: '24/7',
      description: 'Continuous monitoring of every transaction, every vendor, every anomaly.',
    },
    {
      icon: Shield,
      title: 'Interception',
      stat: '< 2min',
      description: 'Flagged duplicates before they leave your account. Not after.',
    },
    {
      icon: Lock,
      title: 'Protection',
      stat: '99.2%',
      description: 'Accuracy that catches what manual review misses.',
    },
  ];

  // What we catch - the gallery
  const catches = [
    { label: 'Duplicate Invoices', amount: '₹12.4L', frequency: 'avg. caught per month' },
    { label: 'Vendor Overcharges', amount: '₹4.2L', frequency: 'identified quarterly' },
    { label: 'Timing Anomalies', amount: '₹8.1L', frequency: 'prevented annually' },
    { label: 'Identity Mismatches', amount: '₹2.8L', frequency: 'flagged per quarter' },
  ];

  return (
    <>
      <SEO 
        title="Sentinel – ₹2.3 Crore Saved | Financial Reconciliation for India"
        description="Average recovery for finance teams in their first 90 days. Sentinel catches what spreadsheets miss. Trusted by India's leading enterprises."
        canonical={SITE_URL}
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
        {/* Subtle grid overlay - The Silent Audit motif */}
        <div 
          className="fixed inset-0 opacity-[0.02] dark:opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                              linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
          aria-hidden="true"
        />
        
        {/* Ambient glow - top */}
        <div 
          className="fixed top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-primary/5 rounded-full blur-[150px] pointer-events-none"
          aria-hidden="true"
        />

        {/* Navigation - Minimal, confident */}
        <nav className="relative z-20 container mx-auto px-6 py-6" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <img 
                  src={logo} 
                  alt="Sentinel" 
                  className="w-9 h-9 brightness-0 dark:brightness-100 dark:invert transition-transform duration-300 group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <span className="text-xl font-bold tracking-tight">SENTINEL</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Button 
                onClick={() => navigate('/auth')} 
                className="rounded-2xl px-6 h-11 font-medium shadow-sm hover:shadow-md transition-all"
              >
                Sign In
              </Button>
            </div>
          </div>
        </nav>

        {/* Main content landmark for accessibility */}
        <main>
        {/* HERO: The Silent Audit - Proof-first */}
        <header ref={heroRef} className="relative z-10 container mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-32">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial="hidden"
            animate={isHeroInView ? "visible" : "hidden"}
            variants={stagger}
          >
            {/* The Proof Number - Monumental */}
            <motion.div variants={slideUp} className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 border border-success/20 text-success mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                </span>
                <span className="text-sm font-medium">Live Protection Active</span>
              </div>
              
              <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold tracking-tighter leading-[0.9]">
                <span className="bg-gradient-primary bg-clip-text text-transparent">₹2.3 Cr</span>
                <span className="text-foreground/20 ml-2 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light">saved.</span>
              </h1>
            </motion.div>

            {/* Subhead - Understated confidence */}
            <motion.p 
              variants={slideUp}
              className="text-xl md:text-2xl text-muted-foreground max-w-2xl leading-relaxed mb-12"
            >
              Average recovery for finance teams in their first 90 days. 
              <span className="text-foreground font-medium"> Sentinel catches what spreadsheets miss.</span>
            </motion.p>

            {/* CTAs - Clean, confident */}
            <motion.div variants={slideUp} className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/auth')} 
                className="text-lg px-8 h-14 rounded-2xl shadow-lg hover:shadow-xl transition-all group"
              >
                Start the Audit
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => navigate('/features')} 
                className="text-lg px-8 h-14 rounded-2xl border-border/50 hover:border-primary/30 hover:bg-surface-elevated transition-all group"
              >
                <Play className="mr-2 w-4 h-4" />
                See Live Demo
              </Button>
            </motion.div>

            {/* Trust line - Minimal */}
            <motion.p variants={fadeIn} className="mt-8 text-sm text-muted-foreground">
              No credit card required • 14-day trial • SOC 2 practices
            </motion.p>
          </motion.div>
        </header>

        {/* PROOF SECTION: Live Ticker + What We Catch */}
        <section ref={proofRef} className="relative z-10 py-20 border-t border-border/30">
          <div className="container mx-auto px-6">
            <motion.div
              initial="hidden"
              animate={isProofInView ? "visible" : "hidden"}
              variants={stagger}
              className="max-w-6xl mx-auto"
            >
              {/* Section header */}
              <motion.div variants={slideUp} className="text-center mb-16">
                <p className="text-sm font-medium text-primary tracking-wide uppercase mb-3">What We Catch</p>
                <h2 className="text-3xl md:text-4xl font-bold">
                  The leaks your team can't see
                </h2>
              </motion.div>

              {/* Catches Gallery - Clean cards */}
              <motion.div 
                variants={stagger}
                className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
              >
                {catches.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={scaleIn}
                    className="group relative p-6 rounded-2xl bg-surface-elevated border border-border/50 hover:border-primary/30 transition-all duration-300"
                  >
                    <div className="text-3xl font-bold text-primary tabular-nums mb-1">
                      {item.amount}
                    </div>
                    <div className="font-semibold text-foreground mb-2">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{item.frequency}</div>
                    
                    {/* Subtle hover glow */}
                    <div className="absolute inset-0 rounded-2xl bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* THREE LAYERS OF DEFENSE */}
        <section ref={defenseRef} className="relative z-10 py-24 bg-surface/50">
          <div className="container mx-auto px-6">
            <motion.div
              initial="hidden"
              animate={isDefenseInView ? "visible" : "hidden"}
              variants={stagger}
              className="max-w-6xl mx-auto"
            >
              {/* Section header */}
              <motion.div variants={slideUp} className="text-center mb-20">
                <p className="text-sm font-medium text-primary-dark dark:text-primary tracking-wide uppercase mb-3">The Defense System</p>
                <h2 className="text-3xl md:text-5xl font-bold mb-4">
                  Three layers between you and loss
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Every transaction passes through our silent audit. Nothing slips through.
                </p>
              </motion.div>

              {/* Defense layers - Horizontal on desktop */}
              <motion.div 
                variants={stagger}
                className="grid md:grid-cols-3 gap-8"
              >
                {defenseLayers.map((layer, i) => (
                  <motion.div
                    key={i}
                    variants={scaleIn}
                    className="relative group"
                  >
                    <div className="relative p-8 rounded-3xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-500 h-full">
                      {/* Layer number - Security aesthetic (decorative only) */}
                      <div className="absolute top-6 right-6 text-6xl font-bold text-transparent tabular-nums select-none pointer-events-none" aria-hidden="true">
                        0{i + 1}
                      </div>
                      
                      {/* Icon */}
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/15 transition-colors">
                        <layer.icon className="w-7 h-7 text-primary" />
                      </div>
                      
                      {/* Stat - Monospace for authority */}
                      <div className="text-4xl font-bold text-foreground mb-2 font-mono">
                        {layer.stat}
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-xl font-bold mb-3">{layer.title}</h3>
                      
                      {/* Description */}
                      <p className="text-muted-foreground leading-relaxed">
                        {layer.description}
                      </p>
                    </div>
                    
                    {/* Connector line - only on desktop, not last */}
                    {i < 2 && (
                      <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-border/50" />
                    )}
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CASE STUDY / TESTIMONIAL - Deep dive, single focus */}
        <section className="relative z-10 py-24 border-t border-border/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={stagger}
                className="relative"
              >
                {/* Quote marks - subtle */}
                <div className="absolute -top-8 -left-4 text-8xl text-primary/10 font-serif" aria-hidden="true">
                  "
                </div>
                
                <motion.blockquote 
                  variants={slideUp}
                  className="text-2xl md:text-3xl lg:text-4xl font-medium leading-relaxed text-foreground mb-8"
                >
                  Sentinel prevented{' '}
                  <span className="text-primary font-bold">₹2.3 crore</span>{' '}
                  in duplicate payments in just 3 months. The ROI is incredible—we caught errors our 
                  senior accountants missed for years.
                </motion.blockquote>
                
                <motion.div variants={fadeIn} className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-xl shadow-glow">
                    R
                  </div>
                  <div>
                    <cite className="text-lg font-bold not-italic">Rajesh Kumar</cite>
                    <div className="text-muted-foreground">CFO, TechCorp India</div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FINAL CTA - The Vault Opens */}
        <section className="relative z-10 py-24 mb-12">
          <div className="container mx-auto px-6">
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={scaleIn}
              className="max-w-4xl mx-auto relative"
            >
              <div className="relative p-12 md:p-16 rounded-3xl bg-gradient-to-br from-foreground to-foreground/90 dark:from-card dark:to-card/90 overflow-hidden">
                {/* Grid overlay - vault aesthetic */}
                <div 
                  className="absolute inset-0 opacity-[0.03]"
                  style={{
                    backgroundImage: `linear-gradient(hsl(var(--background)) 1px, transparent 1px),
                                      linear-gradient(90deg, hsl(var(--background)) 1px, transparent 1px)`,
                    backgroundSize: '40px 40px'
                  }}
                  aria-hidden="true"
                />
                
                <div className="relative z-10 text-center">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background dark:text-foreground mb-4">
                    Stop the leak.
                  </h2>
                  <p className="text-lg text-background/70 dark:text-muted-foreground max-w-xl mx-auto mb-8">
                    Join 500+ finance teams who've recovered millions with automated reconciliation.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg" 
                      onClick={() => navigate('/auth')} 
                      className="text-lg px-10 h-14 bg-background text-foreground hover:bg-background/90 dark:bg-primary dark:text-primary-foreground rounded-2xl shadow-lg"
                    >
                      Start the Audit
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      onClick={() => navigate('/support')} 
                      className="text-lg px-10 h-14 border-background/30 text-background hover:bg-background/10 dark:border-border dark:text-foreground rounded-2xl"
                    >
                      Talk to Sales
                    </Button>
                  </div>
                  
                  <p className="mt-6 text-sm text-background/60 dark:text-muted-foreground">
                    No credit card required • Setup in 10 minutes
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQSection />
        </main>

        {/* Footer - Clean, authoritative */}
        <footer className="relative z-10 border-t border-border/30 bg-surface/30" role="contentinfo">
          <div className="container mx-auto px-6 py-16">
            <div className="grid md:grid-cols-4 gap-12 mb-12">
              {/* Brand */}
              <div>
                <Link to="/" className="flex items-center gap-3 mb-4">
                  <img 
                    src={logo} 
                    alt="Sentinel" 
                    className="w-8 h-8 brightness-0 dark:brightness-100 dark:invert" 
                  />
                  <span className="text-xl font-bold tracking-tight">SENTINEL</span>
                </Link>
                <p className="text-sm text-muted-foreground">
                  Financial intelligence that pays for itself.
                </p>
              </div>
              
              {/* Product */}
              <div>
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wide text-muted-foreground">Product</h3>
                <nav className="space-y-3">
                  <Link to="/features" className="block text-sm text-foreground hover:text-primary transition-colors">
                    Features
                  </Link>
                  <Link to="/pricing" className="block text-sm text-foreground hover:text-primary transition-colors">
                    Pricing
                  </Link>
                  <Link to="/blog" className="block text-sm text-foreground hover:text-primary transition-colors">
                    Blog
                  </Link>
                </nav>
              </div>
              
              {/* Resources */}
              <div>
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wide text-muted-foreground">Resources</h3>
                <nav className="space-y-3">
                  <Link to="/documentation" className="block text-sm text-foreground hover:text-primary transition-colors">
                    Documentation
                  </Link>
                  <Link to="/api-reference" className="block text-sm text-foreground hover:text-primary transition-colors">
                    API Reference
                  </Link>
                  <Link to="/support" className="block text-sm text-foreground hover:text-primary transition-colors">
                    Support
                  </Link>
                </nav>
              </div>
              
              {/* Company */}
              <div>
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wide text-muted-foreground">Company</h3>
                <nav className="space-y-3">
                  <Link to="/about" className="block text-sm text-foreground hover:text-primary transition-colors">
                    About
                  </Link>
                  <Link to="/privacy" className="block text-sm text-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="block text-sm text-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </nav>
              </div>
            </div>
            
            {/* Bottom bar */}
            <div className="border-t border-border/30 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © 2025 Sentinel. All rights reserved.
              </p>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>🇮🇳 Made for India</span>
                <span className="text-border">•</span>
                <span>Built by{' '}
                  <a 
                    href="https://bwestudios.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-semibold text-foreground hover:text-primary transition-colors"
                  >
                    BWE Studio
                  </a>
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Index;
